<template>
  <div>
    <div class="wrapper">
      <breadcrumb :crumbs="[$t('menu.stockIns'), $t('text.list')]">
      </breadcrumb>
    </div>
    <narrow
      :narrows="narrows"
      :init-filter="initFilter"
      @narrowSelect="narrowSelect"
    />
    <div class="search with-narrows">
      <el-form
        :inline="true"
        @submit.native.prevent="search"
        :model="searchForm"
        size="small"
      >
        <el-form-item>
          <el-input
            v-model.trim="searchForm.keyword"
            :placeholder="$t('material.serialNoName')"
            size="small"
          />
        </el-form-item>
        <el-form-item>
          <el-input
            v-model.trim="searchForm.remark"
            :placeholder="$t('text.remark')"
            size="small"
          />
        </el-form-item>
        <el-form-item>
          <el-input
            v-model.trim="searchForm.shelf"
            :placeholder="$t('menu.shelfs')"
            size="small"
          />
        </el-form-item>
        <el-form-item>
          <el-input
            v-model.trim="searchForm.associateSerialNo"
            :placeholder="$t('stock.associateSerialNo')"
            size="small"
          />
        </el-form-item>
        <date-range
          v-model="searchForm"
          type="datetime"
          field="created"
          :start-placeholder="$t('rules.startAt', [$t('stock.createAtIn')])"
          :end-placeholder="$t('rules.endAt', [$t('stock.createAtIn')])"
        />
        <el-form-item>
          <el-select
            v-model="searchForm.supplierId"
            :placeholder="$t('menu.suppliers')"
            size="small"
            filterable
            clearable
          >
            <el-option
              v-for="supplier in suppliers"
              :key="supplier.id"
              :label="supplier.name"
              :value="supplier.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button
            type="primary"
            icon="el-icon-search"
            native-type="submit"
          />
        </el-form-item>
      </el-form>
    </div>
    <div class="wrapper table-area">
      <sticky :sticky-top="50" :viewport="1024" :is-rem="false" class="sticky">
        <vxe-toolbar ref="recordsToolbar" custom zoom class="table-toolbar">
          <template v-slot:buttons>
            <el-dropdown class="mgr10" @command="download">
              <el-button type="default" size="mini">
                {{ $t("action.download") }}
                <i class="el-icon-caret-bottom el-icon--right" />
              </el-button>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item command="checked">
                  {{ $t("action.downLoadChecked") }}
                </el-dropdown-item>
                <el-dropdown-item command="search">
                  {{ $t("action.downLoadSearch") }}
                </el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
            <el-dropdown @command="print">
              <el-button type="default" size="mini">
                {{ $t("action.print") }}
                <i class="el-icon-caret-bottom el-icon--right" />
              </el-button>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item command="printListDiv">{{
                  $t("action.printList")
                }}</el-dropdown-item>
                <el-dropdown-item command="printByAssociateSerialNoDiv">{{
                  $t("action.printListByAssociateSerialNo")
                }}</el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
            <span class="pager-total">
              {{ $t("rules.pagerTotal", [pager.total]) }}
            </span>
          </template>
        </vxe-toolbar>
      </sticky>

      <vxe-table
        :data="records"
        @checkbox-change="setSelected"
        @checkbox-all="setSelected"
        :custom-config="{ storage: true, checkMethod: checkColumnMethod }"
        :loading="loading"
        id="materialTable"
        row-id="id"
        ref="recordsTable"
        class="recordsTable"
        size="mini"
        resizable
        round
        auto-resize
        highlight-hover-row
        :scroll-y="{ enabled: false }"
      >
        <vxe-table-column field="checkbox" type="checkbox" width="35" />
        <vxe-table-column
          field="image"
          :title="$t('product.images')"
          width="80"
        >
          <template v-slot="{ row }">
            <thumbnail :images="row.image" />
          </template>
        </vxe-table-column>
        <vxe-table-column
          field="serialNo"
          :title="$t('material.serialNo')"
          min-width="100"
        >
          <template v-slot="{ row }">
            {{ row.materialSerialNo }}
            <div class="font10">{{ row.name }} {{ row.subjectName }}</div>
          </template>
        </vxe-table-column>
        <vxe-table-column
          field="color"
          :title="$t('material.color')"
          min-width="100"
        >
          <template v-slot="{ row }">
            {{ row.color }}

            <div class="font10">{{ row.colorNo }}</div>
          </template>
        </vxe-table-column>
        <vxe-table-column
          field="quantity"
          :title="$t('text.quantity')"
          min-width="100"
        >
          <template v-slot="{ row }">
            <span v-if="row.unit == row.inUnit">
              {{ row.quantity }}{{ row.unit }}
            </span>
            <span v-else>
              {{ row.quantity }}{{ row.unit }} = {{ row.inQuantity
              }}{{ row.inUnit }}
            </span>
          </template>
        </vxe-table-column>
        <vxe-table-column
          field="remainQuantity"
          :title="$t('inventory.remainQuantity')"
          min-width="80"
        >
          <template v-slot="{ row }">
            <span v-if="row.unit == row.inUnit">
              {{ row.remainQuantity }}{{ row.unit }}
            </span>
            <span v-else>
              {{ row.remainQuantity }}{{ row.unit }} = {{ row.inRemainQuantity
              }}{{ row.inUnit }}
            </span>
          </template>
        </vxe-table-column>
        <vxe-table-column
          field="price"
          :title="$t('material.outPrice')"
          min-width="90"
        >
          <template v-slot="{ row }">
            {{ row.price | currency4 }}
          </template>
        </vxe-table-column>
        <vxe-table-column
          field="subtotal"
          :title="$t('order.subtotal')"
          min-width="90"
        >
          <template v-slot="{ row }">
            {{ (row.quantity * row.price) | currency4 }}
          </template>
        </vxe-table-column>
        <vxe-table-column
          field="supplierName"
          :title="$t('menu.suppliers')"
          min-width="80"
        />
        <vxe-table-column
          field="type"
          :title="$t('stock.stockInType')"
          min-width="110"
        >
          <template v-slot="{ row }">
            <span :class="row.type == 'cash' ? 'orange' : ''">
              {{ $t("stockInTypesMms." + row.type) }}
            </span>
            <div v-if="row.associateSerialNo != null" class="font10">
              {{ $t("stock.associateSerialNo") }}:{{ row.associateSerialNo }}
            </div>
          </template>
        </vxe-table-column>

        <vxe-table-column
          field="warehouseName"
          :title="$t('menu.warehouses')"
          min-width="80"
        >
          <template v-slot="{ row }">
            {{ row.warehouseName }}
            <div class="font10">
              {{ row.shelf }}
            </div>
          </template>
        </vxe-table-column>
        <vxe-table-column
          field="ownerName"
          :title="$t('stock.ownerIn')"
          min-width="100"
        >
          <template v-slot="{ row }">
            {{ row.ownerName }}
            <div class="font10">
              {{ row.createdAt }}
            </div>
          </template>
        </vxe-table-column>
        <vxe-table-column
          field="printAt"
          :title="$t('text.printAt')"
          min-width="100"
        />
        <vxe-table-column
          field="remark"
          :title="$t('text.remark')"
          min-width="100"
        >
          <template v-slot="{ row }">
            <raw-html-limit-number
              :content="row.remark"
              :number="6"
              type="richText"
            />
          </template>
        </vxe-table-column>
      </vxe-table>
      <div class="flex flex-between pager">
        <div class="selected-qty-tips">
          选中了
          <span class="strong">{{ selectRecordQty }}</span>
          条记录
        </div>
        <div>
          <vxe-pager
            size="small"
            :loading="false"
            :background="false"
            :current-page="pager.currentPage"
            :page-size="pager.pageSize"
            :total="pager.total"
            :layouts="pagerConfig.layouts"
            :page-sizes="pagerConfig.pageSizes"
            @page-change="changePager"
          ></vxe-pager>
        </div>
      </div>
    </div>

    <div id="printListDiv" style="display: none">
      <div style="width: 100%; font-size: 12px">
        <div style="text-align: center; font-size: 16px; margin-bottom: 20px">
          {{ $t("menu.stockIns") }}
        </div>
        <div>
          <table
            style="
              width: 100%;
              border-collapse: collapse;
              border: 1px solid black;
              font-size: 12px;
            "
          >
            <thead>
              <tr style="border: 1px solid black">
                <td style="border: 1px solid black; width: 40px">序号</td>
                <td style="border: 1px solid black">
                  {{ $t("material.serialNo") }}
                </td>
                <td style="border: 1px solid black">
                  {{ $t("material.color") }}
                </td>
                <td style="border: 1px solid black">
                  {{ $t("material.quantity") }}
                </td>
                <td style="border: 1px solid black">
                  {{ $t("material.price") }}
                </td>
                <td style="border: 1px solid black">
                  {{ $t("order.subtotal") }}
                </td>
                <td style="border: 1px solid black">
                  {{ $t("stock.stockInType") }}
                </td>
                <td style="border: 1px solid black">
                  {{ $t("menu.warehouses") }}
                </td>
                <td style="border: 1px solid black">
                  {{ $t("stock.createAtIn") }}
                </td>
                <td style="border: 1px solid black">
                  {{ $t("stock.ownerIn") }}
                </td>
                <td style="border: 1px solid black">{{ $t("text.remark") }}</td>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(c, index) of selectRecords"
                :key="index"
                style="border: 1px solid black"
              >
                <td style="border: 1px solid black">{{ index + 1 }}</td>
                <td style="border: 1px solid black">
                  {{ c.materialSerialNo }}
                  <div>{{ c.name }} {{ c.subjectName }}</div>
                </td>
                <td style="border: 1px solid black">
                  {{ c.color }}
                  <div>{{ c.colorNo }}</div>
                </td>
                <td style="border: 1px solid black">
                  {{ c.quantity }}{{ c.unit }}
                </td>
                <td style="border: 1px solid black">
                  {{ c.price | currency4 }}
                </td>
                <td style="border: 1px solid black">
                  {{ (c.price * c.quantity) | currency4 }}
                </td>
                <td style="border: 1px solid black">
                  {{ $t("stockInTypesMms." + c.type) }}
                </td>
                <td style="border: 1px solid black">
                  {{ c.warehouseName }}
                  <div>{{ c.shelf }}</div>
                </td>
                <td style="border: 1px solid black">{{ c.createdAt }}</td>
                <td style="border: 1px solid black">{{ c.ownerName }}</td>
                <td style="border: 1px solid black">
                  <raw-html :content="c.remark" />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div style="margin-left: 20px">
          合计金额：{{ computeTotalPrice() | currency4 }}
        </div>
        <div>
          <span style="float: left; margin-left: 20px">部门经理：</span>
          <span style="float: left; margin-left: 200px">复核人：</span>
          <span style="float: right; margin-right: 20px"
            >制单人：{{ Permit.currentUser().name }}</span
          >
        </div>
      </div>
    </div>

    <el-dialog
      :title="$t('action.print')"
      :close-on-click-modal="false"
      :visible.sync="printByAssociateSerialNoDF"
      width="70%"
    >
      <el-form
        ref="printByAssociateSerialNoForm"
        :model="printByAssociateSerialNoObj"
        label-width="80px"
      >
        <table class="table" cellspacing="0" cellpadding="0" border="0">
          <thead class="thead">
            <tr>
              <th style="min-width: 100px">
                {{ $t("material.serialNo") }}
              </th>
              <th style="min-width: 100px">{{ $t("material.color") }}</th>
              <th style="min-width: 60px">{{ $t("text.quantity") }}</th>
              <th style="min-width: 90px">
                {{ $t("inventory.remainQuantity") }}
              </th>
              <th style="min-width: 40px">{{ $t("material.price") }}</th>
              <th style="min-width: 40px">{{ $t("order.subtotal") }}</th>
              <th style="min-width: 60px">{{ $t("stock.stockInType") }}</th>
              <th style="min-width: 60px">{{ $t("menu.warehouses") }}</th>
              <th style="min-width: 80px">{{ $t("stock.ownerIn") }}</th>
              <th style="min-width: 100px">{{ $t("text.printAt") }}</th>
              <th style="min-width: 60px">{{ $t("text.remark") }}</th>
              <th style="width: 40px">{{ $t("text.action") }}</th>
            </tr>
          </thead>
          <tbody
            v-for="(record, index) in printByAssociateSerialNo"
            :key="index"
            class="tbody"
          >
            <tr class="tbody-total">
              <td colspan="11">
                {{ $t("stock.associateSerialNo") }}:{{
                  record.associateSerialNo || "无"
                }}
              </td>
              <td>
                <el-button
                  type="text"
                  size="small"
                  @click="removePrintByAssociateSerialNoRecord(index)"
                  >{{ $t("text.remove") }}</el-button
                >
              </td>
            </tr>
            <tr v-for="(item, itemIndex) in record.items" :key="itemIndex">
              <td>
                {{ item.materialSerialNo }}
                <div class="font10">{{ item.name }} {{ item.subjectName }}</div>
              </td>
              <td>
                {{ item.color }}
                <div class="font10">{{ item.colorNo }}</div>
              </td>
              <td>{{ item.quantity }}{{ item.unit }}</td>
              <td>
                <span v-if="item.remainQuantity != null"
                  >{{ item.remainQuantity }}{{ item.unit }}</span
                >
              </td>
              <td>{{ item.price | currency4 }}</td>
              <td>{{ (item.quantity * item.price) | currency4 }}</td>
              <td>
                {{ $t("stockInTypesMms." + item.type) }}
                <div v-if="item.supplierName != null" class="font10">
                  {{ $t("menu.suppliers") }}:{{ item.supplierName }}
                </div>
                <div v-if="item.associateSerialNo != null" class="font10">
                  {{ $t("stock.associateSerialNo") }}:{{
                    item.associateSerialNo
                  }}
                </div>
              </td>
              <td>
                {{ item.warehouseName }}
                <div class="font10">{{ item.shelf }}</div>
              </td>
              <td>
                {{ item.ownerName }}
                <div class="font10">{{ item.createdAt }}</div>
              </td>
              <td>{{ item.printAt }}</td>
              <td>
                <raw-html-limit-number :content="item.remark" :number="20" />
              </td>
              <td>
                <el-button
                  type="text"
                  size="small"
                  @click="removePrintByAssociateSerialNoItem(index, itemIndex)"
                  >{{ $t("text.remove") }}</el-button
                >
              </td>
            </tr>
          </tbody>
        </table>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button
          type="primary"
          plain
          :loading="printByAssociateSerialNoDoing"
          size="small"
          @click="printByAssociateSerialNoSubmit"
          >{{ $t("text.submit") }}</el-button
        >
        <el-button size="small" @click="printByAssociateSerialNoDF = false">{{
          $t("text.close")
        }}</el-button>
      </div>
    </el-dialog>

    <div id="printByAssociateSerialNoDiv" style="display: none">
      <div style="width: 100%; font-size: 12px">
        <div v-for="(record, index) of printByAssociateSerialNo" :key="index">
          <div style="text-align: center; font-size: 16px; margin-bottom: 20px">
            {{ record.subjectNames.join("/") }}入库单
          </div>
          <div>
            <span style="margin-right: 90px"
              >{{ $t("menu.warehouses") }}:
              {{ record.warehouseNames.join("/") }}</span
            >
            {{ $t("menu.suppliers") }}: {{ record.supplierNames.join("/") }}
            <span style="float: right">
              {{ $t("stock.associateSerialNo") }}:
              <span v-if="record.associateSerialNo != null">{{
                record.associateSerialNo
              }}</span>
              <span v-else style="margin-right: 90px">&nbsp;</span>
            </span>
          </div>
          <div>
            <table
              style="
                width: 100%;
                border-collapse: collapse;
                border: 1px solid black;
                font-size: 12px;
              "
            >
              <thead>
                <tr style="border: 1px solid black">
                  <td style="border: 1px solid black; width: 20px">序号</td>
                  <td style="border: 1px solid black; width: 180px">
                    {{ $t("material.serialNo") }}
                  </td>
                  <td style="border: 1px solid black; width: 60px">
                    {{ $t("material.color") }}
                  </td>
                  <td style="border: 1px solid black; width: 40px">
                    {{ $t("material.quantity") }}
                  </td>
                  <td style="border: 1px solid black; width: 40px">
                    {{ $t("material.price") }}
                  </td>
                  <td style="border: 1px solid black; width: 40px">
                    {{ $t("order.subtotal") }}
                  </td>
                  <td style="border: 1px solid black; width: 80px">
                    {{ $t("stock.stockInType") }}
                  </td>
                  <!-- <td style="border: 1px solid black; width: 80px">{{ $t('menu.warehouses') }}</td> -->
                  <td style="border: 1px solid black; width: 80px">
                    {{ $t("stock.createAtIn") }}
                  </td>
                  <!-- <td style="border: 1px solid black">{{ $t('stock.ownerNameIn') }}</td> -->
                  <!-- <td style="border: 1px solid black">{{ $t('text.remark') }}</td> -->
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(c, indexItem) of record.items"
                  :key="c.id"
                  style="border: 1px solid black"
                >
                  <td style="border: 1px solid black">{{ indexItem + 1 }}</td>
                  <td style="border: 1px solid black">
                    {{ c.materialSerialNo }} {{ c.name }} {{ c.subjectName }}
                    <!-- <div>{{ c.name }} {{ c.subjectName }}</div> -->
                  </td>
                  <td style="border: 1px solid black">
                    {{ c.color }}({{ c.colorNo }})
                  </td>
                  <td style="border: 1px solid black">
                    {{ c.quantity }}{{ c.unit }}
                  </td>
                  <td style="border: 1px solid black">
                    {{ c.price | currency4 }}
                  </td>
                  <td style="border: 1px solid black">
                    {{ (c.price * c.quantity) | currency4 }}
                  </td>
                  <td style="border: 1px solid black">
                    {{ $t("stockInTypesMms." + c.type) }}
                  </td>
                  <!-- <td style="border: 1px solid black">
                    {{ c.warehouseName }}
                    <div>{{ c.shelf }}</div>
                  </td>-->
                  <td style="border: 1px solid black">{{ c.createdAt }}</td>
                  <!-- <td style="border: 1px solid black">{{ c.ownerName }}</td> -->
                  <!-- <td style="border: 1px solid black">
                    <raw-html :content="c.remark" />
                  </td>-->
                </tr>
              </tbody>
            </table>
          </div>
          <div style="margin-left: 20px">
            合计金额：{{ record.totalPrice | currency4 }}
          </div>
          <div>
            <span style="float: left; margin-left: 20px">部门经理：</span>
            <span style="float: left; margin-left: 200px">复核人：</span>
            <span style="float: right; margin-right: 20px"
              >制单人：{{ Permit.currentUser().name }}</span
            >
          </div>
          <br />
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import axios from "axios";
import { page, table } from "@/utils/mixins.js";
import Qrcode from "@chenfengyuan/vue-qrcode";
import RawHtmlLimitNumber from "@/components/RawHtmlLimitNumber";
import RawHtml from "@/components/RawHtml";
import DateRange from "@/components/DateRange";

import Util from "@/utils/index.js";
import SubjectMms from "@/api/subjectMms.js";
import StockInOutMms from "@/api/stockInOutMms.js";
import Warehouse from "@/api/warehouse.js";
import UserWarehouse from "@/api/userWarehouse.js";
import SupplierMms from "@/api/supplierMms.js";

export default {
  mixins: [
    page,
    table.pager,
    table.search,
    table.narrow,
    table.checker,
    table.sorter,
    table.basic,
  ],
  components: {
    Qrcode,
    RawHtmlLimitNumber,
    RawHtml,
    DateRange,
  },
  data() {
    return {
      Util: Util,
      narrows: [],
      initFilter: null,
      searchForm: {
        keyword: null,
        remark: null,
        createdStartAt: null,
        createdEndAt: null,
        shelf: null,
        associateSerialNo: null,
        supplierId: null,
      },
      sorter: {
        sortBy: "stockInOuts_id",
        sortIndicator: "desc",
      },
      warehouses: [],
      userWarehouses: [],
      mmsAuthWarehouses: [],
      subjects: [],
      suppliers: [],

      //
      type: null,
      printByAssociateSerialNoDF: false,
      printByAssociateSerialNo: [],
      printByAssociateSerialNoObj: {},
      printByAssociateSerialNoDoing: false,
      inWarehouseListByAssociateSerialNoSubjectNames: null,
    };
  },
  computed: {
    implicit() {
      return {
        stockType: "in",
      };
    },
  },
  created() {
    const self = this;
    this.$nextTick(() => {
      // 手动将表格和工具栏进行关联
      this.$refs.recordsTable.connect(this.$refs.recordsToolbar);
    });
    self.narrowsStockInType();
    self.loadingStart();
    axios
      .all([
        SubjectMms.findByTenantId(),
        Warehouse.findByTenantId(),
        UserWarehouse.findByUserId(self.Permit.currentUser().id),
        SupplierMms.findByTenantId(),
      ])
      .then(
        axios.spread(function (
          subjects,
          warehouses,
          userWarehouses,
          suppliers
        ) {
          if (subjects.status === 200) {
            if (self.Permit.currentUser().admin) {
              self.subjects = subjects.data;
            } else {
              self.subjects = subjects.data.filter((s) =>
                self.Permit.mmsAuthSubjuctIds().includes(s.id)
              );
            }
            self.narrowsSubjects();
          } else {
            self.sysError();
          }
          self.warehouses = warehouses.data;
          self.userWarehouses = userWarehouses.data;
          if (self.Permit.currentUser().admin) {
            self.mmsAuthWarehouses = self.warehouses;
          } else {
            const userWarehouseIds = self.userWarehouses.map(
              (u) => u.warehouseId
            );
            self.mmsAuthWarehouses = self.warehouses.filter((w) =>
              userWarehouseIds.includes(w.id)
            );
          }
          self.narrowsWarehouses();
          if (suppliers.status === 200) {
            self.suppliers = suppliers.data;
          } else {
            self.sysError();
          }
          self.loadingEnd();
        })
      );
  },
  methods: {
    narrowsStockInType() {
      if (this.Permit.currentTenant().config.mmsInvUseCash == true) {
        this.narrows.push([
          {
            label: this.$t("stock.stockInType"),
            items: StockInOutMms.in_types.map((t) => {
              return {
                key: t,
                label: this.$t("stockInTypesMms." + t),
                condition: { type: t },
              };
            }),
          },
          {
            label: this.$t("text.printInfo"),
            items: [
              {
                label: this.$t("printInfos.yes"),
                key: "yes",
                condition: { print: "yes" },
              },
              {
                label: this.$t("printInfos.no"),
                key: "no",
                condition: { print: "no" },
              },
            ],
          },
        ]);
      } else {
        this.narrows.push([
          {
            label: this.$t("stock.stockInType"),
            items: StockInOutMms.in_types_no_cash.map((t) => {
              return {
                key: t,
                label: this.$t("stockInTypesMms." + t),
                condition: { type: t },
              };
            }),
          },
          {
            label: this.$t("text.printInfo"),
            items: [
              {
                label: this.$t("printInfos.yes"),
                key: "yes",
                condition: { print: "yes" },
              },
              {
                label: this.$t("printInfos.no"),
                key: "no",
                condition: { print: "no" },
              },
            ],
          },
        ]);
      }
    },
    print(type) {
      const self = this;
      self.type = type;
      if (self.selectRecords.length === 0) {
        self.$message.error(this.$t("prompt.atLeastOneData"));
        return;
      }
      if (type == "printListDiv") {
        self.printFun(self.selectRecords.map((c) => c.id));
        return;
      }
      if (type === "printByAssociateSerialNoDiv") {
        const associateSerialNos = self.selectRecords.map(
          (c) => c.associateSerialNo
        );
        const associateSerialNosSet = [...new Set(associateSerialNos)];
        self.printByAssociateSerialNo = associateSerialNosSet.map((s) => {
          return {
            associateSerialNo: s,
            subjectNames: [],
            warehouseNames: [],
            supplierNames: [],
            totalPrice: 0,
            items: self.selectRecords.filter((c) => c.associateSerialNo === s),
          };
        });
        self.computePrintByAssociateSerialNo();
        self.printByAssociateSerialNoDF = true;
      }
    },
    computePrintByAssociateSerialNo() {
      for (const record of this.printByAssociateSerialNo) {
        record.subjectNames = [
          ...new Set(record.items.map((i) => i.subjectName)),
        ];
        record.warehouseNames = [
          ...new Set(record.items.map((i) => i.warehouseName)),
        ];
        record.supplierNames = [
          ...new Set(
            record.items.map((i) => i.supplierName).filter((i) => i != null)
          ),
        ];
        for (const i of record.items) {
          record.totalPrice = record.totalPrice + i.price * i.quantity;
        }
      }
    },
    removePrintByAssociateSerialNoRecord(index) {
      this.printByAssociateSerialNo.splice(index, 1);
      this.computePrintByAssociateSerialNo();
    },
    removePrintByAssociateSerialNoItem(index, itemIndex) {
      this.printByAssociateSerialNo[index].items.splice(itemIndex, 1);
      for (const p of this.printByAssociateSerialNo) {
        if (p.items.length === 0) {
          this.printByAssociateSerialNo.splice(index, 1);
        }
      }
      this.computePrintByAssociateSerialNo();
    },

    printByAssociateSerialNoSubmit() {
      const self = this;
      if (self.printByAssociateSerialNo.length === 0) {
        this.$message.error(this.$t("prompt.atLeastOneData"));
        return;
      }
      const ids = [];
      for (const record of self.printByAssociateSerialNo) {
        for (const item of record.items) {
          ids.push(item.id);
        }
      }
      self.printFun(ids);
    },
    printFun(ids) {
      const self = this;
      StockInOutMms.batchPrint(ids).then(function (res) {
        if (res.status === 200) {
          self.$message({
            message: self.$t("prompt.printSuccess"),
            type: "success",
          });
          self.printByAssociateSerialNoDF = false;
          self.printFunction(self.type);
          self.loadData();
        } else {
          self.sysError();
        }
      });
    },
    computeTotalPrice() {
      if (this.selectRecords.length === 0) {
        return 0;
      }
      let taotal = 0;
      for (const c of this.selectRecords) {
        taotal = taotal + c.price * c.quantity;
        return taotal;
      }
    },

    // 下载
    download(type) {
      const self = this;
      if (type == "checked") {
        if (!self.downloadChecked(self.selectRecords)) {
          return;
        }
        const data = {
          currentPage: 1,
          pageSize: self.limitNumber(),
          ids: self.selectRecords.map((c) => c.id),
        };
        self.downloadData(data);
      } else {
        if (!self.downloadSearch(self.pager.total)) {
          return;
        }
        const data = JSON.parse(JSON.stringify(self.combineParams()));
        data.pageSize = self.limitNumber();
        self.downloadData(data);
      }
    },
    downloadData(data) {
      const self = this;
      StockInOutMms.exportExcel(data).then(function (res) {
        if (res.data.code === 200) {
          self.$message({
            message: self.$t("prompt.downloadPrompt"),
            type: "success",
          });
          self.loadData();
          self.$eventHub.$emit("emitDownLoad");
        } else {
          self.$message.error(self.$t("prompt.downloadErr"));
        }
      });
    },

    narrowsWarehouses() {
      const self = this;
      if (self.mmsAuthWarehouses.length != 0) {
        self.narrows.push([
          {
            label: self.$t("menu.warehouses"),
            items: self.mmsAuthWarehouses.map((warehouse) => {
              return {
                label: warehouse.name,
                key: warehouse.id,
                condition: { warehouseId: warehouse.id },
              };
            }),
          },
        ]);
      }
    },
    narrowsSubjects() {
      const self = this;
      self.narrows.push([
        {
          label: self.$t("material.subject"),
          items: self.subjects.map((subject) => {
            return {
              label: subject.name,
              key: subject.id,
              condition: { subjectId: subject.id },
            };
          }),
        },
      ]);
    },
    //
    dataProvider(params) {
      return StockInOutMms.search(params);
    },
  },
};
</script>
<style scoped>
.table {
  width: 100%;
  font-size: 14px;
}

.thead tr {
  height: 40px;
  line-height: 40px;
  color: #909399;
  font-weight: 500;
}

.tbody tr td {
  padding: 4px;
}
.tbody {
  color: #606266;
}
.tbody-total {
  background: #eff8ff;
}
.thead th,
.tbody td {
  border-bottom: 1px solid #ebeef5;
  text-align: left;
  padding-left: 6px;
}
</style>